import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { Bell, Car, ClipboardList, LayoutDashboard, LogOut, Moon, Shield, Sun, Users, Wrench, Settings, ScrollText, Building2, X, FileText, Camera, Package, ShieldAlert, BarChart3, ClipboardCheck, MoreHorizontal } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { api, ROLE_LABELS } from "@/lib/api";
import { cn } from "@/lib/utils";
import { GlobalSearch } from "@/components/common/GlobalSearch";
import { startQueueSync } from "@/lib/offlineQueue";
import { toast } from "sonner";

const WORK = ["admin", "fleet_manager", "garage_supervisor"];
const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/vehicles", label: "Vehicles", icon: Car, id: "vehicles" },
  { to: "/issues", label: "Issues", icon: ClipboardList, id: "issues" },
  { to: "/services", label: "Services", icon: Wrench, id: "services" },
  { to: "/reminders", label: "Reminders", icon: Bell, id: "reminders" },
  { to: "/documents", label: "Documents", icon: FileText, id: "documents" },
  { to: "/photos", label: "Photo review", icon: Camera, id: "photos", roles: WORK },
  { to: "/supply", label: "Parts & supply", icon: Package, id: "supply", roles: WORK },
  { to: "/risk", label: "Risk & assets", icon: ShieldAlert, id: "risk" },
  { to: "/reports", label: "Reports", icon: BarChart3, id: "reports", roles: ["admin", "fleet_manager"] },
  { to: "/commissioning", label: "Commissioning", icon: ClipboardCheck, id: "commissioning", roles: WORK },
  { to: "/users", label: "Users", icon: Users, id: "users", roles: ["admin"] },
  { to: "/audit", label: "Audit Log", icon: ScrollText, id: "audit", roles: ["admin"] },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
];
const MOBILE_TABS = [NAV[0], NAV[1], NAV[2], NAV[4], { to: "/more", label: "More", icon: MoreHorizontal, id: "more" }];

function Brand() {
  return (
    <div className="flex items-center gap-2 px-3 py-2">
      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Car className="h-4 w-4" /></div>
      <span className="text-base font-semibold tracking-tight">Insideman's Garage</span>
    </div>
  );
}

export function AppShell() {
  const { user, tenant, role, logout, session, endImpersonation } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const items = NAV.filter((n) => !n.roles || n.roles.includes(role));

  useEffect(() => {
    api.get("/notifications").then(({ data }) => setUnread(data.unread)).catch(() => {});
    return startQueueSync((n) => toast.success(`${n} queued photo(s) uploaded`));
  }, []);

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="flex min-h-screen">
      <aside className="hidden w-60 shrink-0 flex-col border-r bg-card p-3 md:flex md:sticky md:top-0 md:h-screen" data-testid="sidebar">
        <Brand />
        <div className="mx-3 mb-3 mt-2 rounded-[11px] bg-muted px-3 py-2" data-testid="tenant-badge">
          <p className="fv-caption">Tenant</p>
          <p className="truncate text-sm font-semibold">{tenant?.name}</p>
        </div>
        <nav className="flex-1 space-y-0.5 overflow-y-auto">
          {items.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.id}`} className={({ isActive }) => cn("fv-nav-item py-1.5", isActive && "active")}>
              <n.icon className="h-4 w-4" />{n.label}
              {n.id === "reminders" && unread > 0 && <span className="ml-auto rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground" data-testid="unread-count">{unread}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="mt-3 border-t pt-3">
          <div className="px-3 py-1">
            <p className="truncate text-sm font-semibold" data-testid="current-user-name">{user?.name}</p>
            <p className="text-xs text-muted-foreground" data-testid="current-user-role">{ROLE_LABELS[role]}</p>
          </div>
          <div className="mt-1 flex items-center gap-1 px-1">
            <button onClick={toggle} data-testid="dark-mode-toggle" className="fv-nav-item flex-1 justify-center" aria-label="Toggle theme">{theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</button>
            <button onClick={doLogout} data-testid="logout-btn" className="fv-nav-item flex-1 justify-center" aria-label="Log out"><LogOut className="h-4 w-4" /></button>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {session?.impersonated_by && (
          <div className="flex items-center justify-between bg-[#FFF8E1] px-4 py-2 text-sm text-[#7A5600] dark:bg-[#3E2723] dark:text-[#FFD54F]" data-testid="impersonation-banner">
            <span className="flex items-center gap-2"><Shield className="h-4 w-4" />Impersonating <strong>{user?.name}</strong> ({tenant?.name}) — all actions are audited</span>
            <button onClick={async () => { await endImpersonation(); navigate("/platform"); }} data-testid="end-impersonation-btn" className="flex items-center gap-1 rounded-lg px-2 py-1 font-medium hover:bg-black/5"><X className="h-4 w-4" />End</button>
          </div>
        )}
        <header className="sticky top-0 z-20 flex flex-wrap items-center justify-between gap-2 border-b bg-card/80 px-4 py-2.5 backdrop-blur-xl md:static md:px-8 md:py-3">
          <div className="md:hidden"><Brand /></div>
          <div className="order-3 w-full md:order-none md:w-auto md:flex-1"><GlobalSearch /></div>
          <div className="flex items-center gap-1 md:hidden">
            <button onClick={toggle} data-testid="dark-mode-toggle-mobile" className="rounded-lg p-2 hover:bg-secondary" aria-label="Toggle theme">{theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</button>
            <button onClick={doLogout} data-testid="logout-btn-mobile" className="rounded-lg p-2 hover:bg-secondary" aria-label="Log out"><LogOut className="h-4 w-4" /></button>
          </div>
        </header>
        <main className="min-w-0 flex-1 overflow-x-hidden px-3 pb-[calc(76px+env(safe-area-inset-bottom))] pt-4 sm:px-4 sm:pt-6 md:px-8 md:pb-10 md:pt-8"><div className="mx-auto min-w-0 max-w-6xl fv-fade-in"><Outlet /></div></main>
        <nav className="fixed inset-x-0 bottom-0 z-20 flex items-stretch border-t bg-card/90 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:hidden" data-testid="bottom-tabs">
          {MOBILE_TABS.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`tab-${n.id}`} className={({ isActive }) => cn("flex min-h-[56px] flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-medium text-muted-foreground", isActive && "text-primary")}>
              <n.icon className="h-5 w-5" />{n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
}

export function PlatformShell() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  return (
    <div className="min-h-screen">
      <header className="flex items-center justify-between border-b bg-card px-4 py-3 md:px-8">
        <div className="flex items-center gap-3"><Brand /><span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary" data-testid="platform-badge"><Building2 className="mr-1 inline h-3 w-3" />Platform Console</span></div>
        <div className="flex items-center gap-2 text-sm">
          <span className="hidden text-muted-foreground sm:inline" data-testid="current-user-name">{user?.name}</span>
          <button onClick={toggle} data-testid="dark-mode-toggle" className="rounded-lg p-2 hover:bg-secondary" aria-label="Toggle theme">{theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}</button>
          <button onClick={async () => { await logout(); navigate("/login"); }} data-testid="logout-btn" className="rounded-lg p-2 hover:bg-secondary" aria-label="Log out"><LogOut className="h-4 w-4" /></button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8 md:px-8 fv-fade-in"><Outlet /></main>
    </div>
  );
}
